import React from "react"
import { Card, CardBody, CardTitle, Row, Col, Progress } from "reactstrap"
import Chart from "react-apexcharts"
import { useTranslation } from "react-i18next"
import IconifyIcon from "components/Shared/IconifyIcon"
import LanguagesDashboardv2 from "./LanguagesDashboardv2"
import { useSelector } from "react-redux"
import { layoutModeTypes } from "../../../constants/layout"
import Colors from "constants/Colors"

const Stat = ({ icon, label, count, percentage, className, layoutMode }) => {
  return (
    <div className={`d-flex align-items-center gap-1 ${className}`}>
      {icon !== null && <IconifyIcon icon={icon} width={23} height={23} />}
      {label !== null && (
        <div className="d-flex flex-column align-items-start">
          <div
            style={{
              textAlign: "start",
              fontSize: "10px",
              minWidth: "50px",
              color:
                layoutMode === layoutModeTypes.LIGHT
                  ? Colors.mutedBlue
                  : Colors.white,
            }}
          >
            {label}
          </div>
          <div className="d-flex gap-1 align-items-center">
            <span
              className="font-semibold"
              style={{
                fontSize: "11px",
                color: layoutMode === layoutModeTypes.LIGHT ? "black" : "white",
              }}
            >
              {count}
            </span>
            <span style={{ fontSize: "10px", color: Colors.green }}>
              ({percentage}%)
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

const NationalityCard = ({ stat, layoutMode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  return (
    <Col sm={3} className="mb-4">
      <Card
        className="rounded-4 text-center p-1"
        style={{ border: "1px solid #7E8492" }}
      >
        <CardBody>
          <div className={language !== "en" ? "text-end" : "text-start"}>
            <div
              className="fw-semibold"
              style={{
                fontSize: "14px",
                color:
                  layoutMode === layoutModeTypes.LIGHT
                    ? Colors.mutedBlue
                    : Colors.white,
              }}
            >
              {stat.title}
            </div>

            <div
              className="h3 fw-bold mb-4"
              style={{
                fontSize: "18px",
                color:
                  layoutMode === layoutModeTypes.LIGHT
                    ? Colors.primary
                    : "white",
              }}
            >
              {stat.value}
            </div>
          </div>

          <Row>
            <Col xs={5} className="d-flex flex-column align-items-start p-0">
              <Stat
                icon={stat.leftIcon}
                label={stat.leftLabel}
                count={stat.leftCount}
                percentage={stat.leftPercentage}
                layoutMode={layoutMode}
              />
            </Col>

            {stat.leftLabel !== null && (
              <Col xs={2} className="d-flex align-items-center p-0">
                <div
                  className="border-start mx-3"
                  style={{
                    height: "40px",
                    background: "#DDE3EE",
                    width: "1px",
                  }}
                ></div>
              </Col>
            )}

            <Col xs={5} className="d-flex flex-column align-items-center p-0">
              <Stat
                className="align-items-center"
                icon={stat.rightIcon}
                label={stat.rightLabel}
                count={stat.rightCount}
                percentage={stat.rightPercentage}
                layoutMode={layoutMode}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>
    </Col>
  )
}

const ReportsDashboardv2 = ({ dashboardData }) => {
  const { t } = useTranslation()
  const layoutMode = useSelector(state => state.Layout.layoutModeType)

  const stats = [
    {
      title: t("Nationality"),
      value: dashboardData?.total_applicants?.toLocaleString() || 0,
      leftIcon: "twemoji:flag-saudi-arabia",
      leftLabel: t("Saudi"),
      leftCount:
        dashboardData?.saudi_or_non_saudi_stats?.total_saudi_candidate?.toLocaleString() ||
        0,
      leftPercentage:
        Math.round(dashboardData?.saudi_or_non_saudi_stats?.saudi_percentage) ||
        0,
      rightIcon: "tabler:world",
      rightLabel: t("Non Saudi"),
      rightCount:
        dashboardData?.saudi_or_non_saudi_stats?.total_non_saudi_candidate?.toLocaleString() ||
        0,
      rightPercentage:
        Math.round(
          dashboardData?.saudi_or_non_saudi_stats?.non_saudi_percentage
        ) || 0,
      accentColor: "#00A3A3",
      bgColor: "rgba(0, 163, 163, 0.1)",
    },
    {
      title: t("Gender"),
      value: dashboardData?.total_applicants?.toLocaleString() || 0,
      leftIcon: "mynaui:male",
      rightIcon: "mage:female",
      leftLabel: t("Male"),
      rightLabel: t("Female"),
      leftCount:
        dashboardData?.male_or_female_stats?.male_count?.toLocaleString() || 0,
      rightCount:
        dashboardData?.male_or_female_stats?.female_count?.toLocaleString() ||
        0,
      leftPercentage:
        Math.round(dashboardData?.male_or_female_stats?.male_percentage) || 0,
      rightPercentage:
        Math.round(dashboardData?.male_or_female_stats?.female_percentage) || 0,
      accentColor: "#FF9800",
      bgColor: "rgba(255, 152, 0, 0.1)",
    },
    {
      title: t("Experience"),
      value: dashboardData?.total_applicants?.toLocaleString() || 0,
      leftIcon: null,
      rightIcon: null,
      leftLabel: t("Experienced"),
      rightLabel: t("New"),
      leftCount:
        dashboardData?.experienced_or_new_stats?.experienced_count?.toLocaleString() ||
        0,
      rightCount:
        dashboardData?.experienced_or_new_stats?.new_count?.toLocaleString() ||
        0,
      leftPercentage:
        Math.round(
          dashboardData?.experienced_or_new_stats?.experienced_percentage
        ) || 0,
      rightPercentage:
        Math.round(dashboardData?.experienced_or_new_stats?.new_percentage) ||
        0,

      accentColor: "#9370DB",
      bgColor: "rgba(147, 112, 219, 0.1)",
    },

    {
      title: t("Applications Withdrawn"),
      value:
        dashboardData?.withrawn_application_stats?.withdrawn_applications_count?.toLocaleString() ||
        0,
      leftIcon: null,
      rightIcon: null,
      leftLabel: null,
      rightLabel: null,
      leftCount: null,
      rightCount: null,
      leftPercentage: null,
      rightPercentage: null,
      accentColor: null,
      bgColor: null,

      // leftCount: (
      //   <>
      //     {t("Withdrawn")}:{" "}
      //     <span className="text-primary">
      //       {Math.round(
      //         dashboardData?.withrawn_application_stats?.withdrawn_percentage
      //       ) || 0}
      //       %
      //     </span>
      //   </>
      // ),

      accentColor: "#D32F2F",
      bgColor: "rgba(211, 47, 47, 0.1)",
    },
  ]

  const options = {
    chart: {
      type: "area",
      toolbar: { show: false },
    },
    grid: {
      // show: false, // Hides all grid lines
    },
    xaxis: {
      categories:
        dashboardData?.weekly_stats?.categories.map(day => t(day)) || [],
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0,
        stops: [20, 100],
      },
    },
    markers: {
      size: 6,
      colors: ["#ffffff"],
      strokeColors: "#00AEEF",
      strokeWidth: 3,
      hover: {
        size: 8,
      },
    },
    colors: ["#00AEEF"],
    tooltip: {
      theme: "light",
    },
    tooltip: {
      y: {
        formatter: value => value.toLocaleString(), // Show formatted numbers on hover
      },
    },
    dataLabels: {
      enabled: true,
      formatter: value => value.toLocaleString(), // Show formatted numbers on the chart
    },
  }

  const series = [
    {
      name: t("Job Applications"),
      data: dashboardData?.weekly_stats?.data || [],
    },
  ]

  const data = {
    labels: dashboardData?.weekly_stats?.categories.map(day => t(day)) || [],
    datasets: [
      {
        label: t("Job Applications"),
        data: dashboardData?.weekly_stats?.data,
        backgroundColor: ["#00778b94"],
        borderColor: "#333",
        borderWidth: 1,
      },
    ],
  }

  return (
    <>
      <div className="d-flex align-items-center gap-2">
        <IconifyIcon
          style={{
            color:
              layoutMode === layoutModeTypes.LIGHT
                ? Colors.primary
                : Colors.primaryLight,
          }}
          icon="solar:user-outline"
        />
        <h3 className="mt-2 font-bold">{t("Applicants")}</h3>
      </div>
      <p
        style={{
          color:
            layoutMode === layoutModeTypes.LIGHT
              ? Colors.mutedBlue
              : Colors.white,
        }}
        className="mb-4"
      >
        {t(
          "General statistics on the demographic characteristics of the project"
        )}
      </p>
      <Row>
        {stats.map((stat, index) => {
          return (
            <NationalityCard stat={stat} key={index} layoutMode={layoutMode} />
          )
        })}
      </Row>
      {/* SPLINE AREA CHART */}
      <Row>
        <Col md={7} sm={12}>
          <Card style={{ border: "1px solid #7E8492" }}>
            <CardBody>
              <div className="d-flex gap-1">
                <IconifyIcon
                  icon="hugeicons:job-search"
                  width={24}
                  height={24}
                  style={{
                    color:
                      layoutMode === layoutModeTypes.LIGHT
                        ? Colors.primary
                        : Colors.primaryLight,
                  }}
                />
                <h4
                  style={{
                    fontWeight: "700",
                    color:
                      layoutMode === layoutModeTypes.LIGHT
                        ? Colors.black
                        : Colors.white,
                  }}
                >
                  {t("Job Applications")}
                </h4>
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color:
                    layoutMode === layoutModeTypes.LIGHT
                      ? Colors.mutedBlue
                      : Colors.white,
                }}
              >
                {t("General statistics on job applications")}
              </div>
              <div className="ltr px-xxl-4">
                <Chart
                  options={options}
                  series={series}
                  type="area"
                  height={500}
                />
                {/* <BarChart data={data} options={options} width={751} height={500} /> */}
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={5} sm={12}>
          <Card style={{ border: "1px solid #7E8492" }}>
            <CardBody>
              <LanguagesDashboardv2 dashboardData={dashboardData} />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default ReportsDashboardv2
